import { inject, Injectable } from "@angular/core";
import { ApiService } from "app/core/api/api.service";
import { BehaviorSubject, Observable, tap } from "rxjs";

export type TeamRole = "admin" | "manager" | "staff";
export type EditableTeamRole = Exclude<TeamRole, "admin">;

export interface TeamUserResponse {
  _id?: string;
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  manager?: {
    venues?: Array<string | { _id: string }>;
  };
}

export type TeamUsersResponse =
  | (TeamUserResponse[] & {
      results?: TeamUserResponse[];
      data?: { users?: TeamUserResponse[] };
    })
  | {
      results?: TeamUserResponse[];
      data?: { users?: TeamUserResponse[] };
    };

export interface TeamMember {
  id: string;
  avatar: string;
  name: string;
  email: string;
  role: TeamRole;
  manager: { venues: string[] };
}

export interface CreateTeamMemberRequest {
  name: string;
  email: string;
  role: TeamRole;
}

export interface UpdateTeamMemberRequest {
  managerId: string;
  role: EditableTeamRole;
  email: string;
  manager: { venues: string[] };
}

export interface UpdateTeamMembersResponse {
  success?: TeamUserResponse[];
  errors?: Array<{ managerId: string; error: string }>;
}

@Injectable({ providedIn: "root" })
export class TeamService {
  private readonly dataSubject = new BehaviorSubject<unknown>(null);
  private readonly apiService = inject(ApiService);

  get data$(): Observable<unknown> {
    return this.dataSubject.asObservable();
  }

  getUsers(): Observable<TeamUsersResponse> {
    return this.apiService
      .get("/users/all")
      .pipe(tap((users) => this.dataSubject.next(users)));
  }

  createTeamMember(data: CreateTeamMemberRequest): Observable<unknown> {
    return this.apiService.post("admin/managers", data);
  }

  deleteTeamMember(memberId: string): Observable<unknown> {
    return this.apiService.delete(`admin/managers/${memberId}`);
  }

  updateTeamMembers(
    managers: UpdateTeamMemberRequest[],
  ): Observable<UpdateTeamMembersResponse> {
    return this.apiService.put("admin/managers", { managers });
  }
}
